import { useState } from 'react'
import { motion } from 'framer-motion'
import { Sparkles, Bot, Database, Zap, Star, ArrowRight } from 'lucide-react'
import EnhancedChatInterface from '../components/EnhancedChatInterface'
import type { BirthData } from '../services/enhancedChatService'

const EnhancedChatDemo = () => {
  const [isChatOpen, setIsChatOpen] = useState(false)
  const [birthData, setBirthData] = useState<BirthData>({
    day: 15,
    month: 7,
    year: 1995,
    hour: 18,
    minute: 45,
    latitude: 34.0522,
    longitude: -118.2437,
    place_of_birth: 'Los Angeles'
  })

  const features = [
    {
      icon: <Database className="w-6 h-6" />,
      title: 'Local RAG System',
      description: '1.4MB of real Swiss Ephemeris data with 225 indexed search terms for instant responses',
      color: 'from-blue-500 to-purple-500'
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: 'Swiss Ephemeris Processing',
      description: 'AI-friendly data conversion with natural language interpretations and confidence scoring',
      color: 'from-purple-500 to-pink-500'
    },
    {
      icon: <Star className="w-6 h-6" />,
      title: 'Enhanced Responses',
      description: 'Personalized astrological insights with source attribution and confidence levels',
      color: 'from-pink-500 to-red-500'
    },
    {
      icon: <Bot className="w-6 h-6" />,
      title: 'Intelligent Fallback',
      description: 'Graceful degradation when external services fail, always providing valuable insights',
      color: 'from-red-500 to-orange-500'
    }
  ]

  const stats = [
    { label: 'Data Points', value: '10 Tested', description: '100% success rate' },
    { label: 'Search Speed', value: '2ms', description: 'Average response time' },
    { label: 'Memory Usage', value: '14.7MB', description: 'Efficient storage' },
    { label: 'Search Terms', value: '225', description: 'Indexed for speed' }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-blue-900">
      {/* Header */}
      <div className="container mx-auto px-4 py-8">
        <div className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center justify-center mb-4"
          >
            <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mr-3">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-white">Enhanced Chat Demo</h1>
          </motion.div>
          <p className="text-xl text-gray-300 mb-8">
            Experience the power of Local RAG + Swiss Ephemeris integration
          </p>
          
          <motion.button
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsChatOpen(true)}
            className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-4 rounded-lg font-semibold text-lg flex items-center space-x-2 hover:from-purple-700 hover:to-pink-700 transition-all duration-200 shadow-lg"
          >
            <Bot className="w-5 h-5" />
            <span>Start Enhanced Chat</span>
            <ArrowRight className="w-5 h-5" />
          </motion.button>
        </div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12"
        >
          {stats.map((stat, index) => (
            <div key={index} className="bg-white/10 backdrop-blur-sm rounded-lg p-6 text-center border border-white/20">
              <div className="text-2xl font-bold text-white mb-1">{stat.value}</div>
              <div className="text-sm text-gray-300 mb-1">{stat.label}</div>
              <div className="text-xs text-gray-400">{stat.description}</div>
            </div>
          ))}
        </motion.div>

        {/* Features */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="grid md:grid-cols-2 gap-6 mb-12"
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 + index * 0.1 }}
              className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20 hover:bg-white/15 transition-all duration-200"
            >
              <div className={`w-12 h-12 bg-gradient-to-r ${feature.color} rounded-lg flex items-center justify-center mb-4`}>
                {feature.icon}
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">{feature.title}</h3>
              <p className="text-gray-300">{feature.description}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Technical Details */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="bg-white/10 backdrop-blur-sm rounded-lg p-8 border border-white/20"
        >
          <h2 className="text-2xl font-bold text-white mb-6 text-center">Technical Architecture</h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Database className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Local RAG System</h3>
              <p className="text-gray-300 text-sm">
                1.4MB of real Swiss Ephemeris data stored locally with 225 indexed search terms for instant retrieval
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Data Processing</h3>
              <p className="text-gray-300 text-sm">
                Swiss Ephemeris Processor converts technical astrological data into AI-friendly natural language
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-pink-500 to-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Star className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Enhanced Responses</h3>
              <p className="text-gray-300 text-sm">
                Confidence scoring, source attribution, and personalized astrological context for every response
              </p>
            </div>
          </div>
        </motion.div>

        {/* Demo Instructions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0 }}
          className="mt-12 text-center"
        >
          <h3 className="text-xl font-semibold text-white mb-4">Try These Questions:</h3>
          <div className="grid md:grid-cols-2 gap-4 max-w-4xl mx-auto">
            {[
              "What are my personality traits?",
              "What are my current astrological influences?",
              "How can I use my strengths today?",
              "What advice do you have for me?",
              "Tell me about my zodiac sign",
              "What should I focus on this week?",
              "How can I improve my relationships?",
              "What career opportunities are coming?"
            ].map((question, index) => (
              <div key={index} className="bg-white/5 rounded-lg p-3 border border-white/10">
                <p className="text-gray-300 text-sm">{question}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Enhanced Chat Interface */}
      <EnhancedChatInterface
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(false)}
        birthData={birthData}
        userId="demo_user"
        title="Rraasi Enhanced Assistant"
        subtitle="Powered by Local RAG & Swiss Ephemeris"
        placeholder="Ask me about your astrological profile..."
      />
    </div>
  )
}

export default EnhancedChatDemo 